import { useCallback, useEffect, useRef, useState } from "react";

import {
  classifySigningFailure,
  isSignatureExpired,
  prepareSignature,
  submitSignature,
  type DocumentWorkflowFileVersion,
  type DocumentWorkflowSignature,
  type DocumentWorkflowSignatureCapabilities,
  type PreparedSignature,
  type SigningFailureKind,
} from "../entities/document-workflow";
import {
  createUnavailableCryptoProAdapter,
  type CadesProfile,
  type SigningCertificate,
} from "../cryptopro";
import type { ShellServices } from "./services";

/** Шаг, на котором сейчас работает сценарий. */
export type SigningStep =
  | "plugin"
  | "certificates"
  | "request"
  | "content"
  | "sign"
  | "send";

export type SigningState =
  | { readonly phase: "idle" }
  | { readonly phase: "working"; readonly step: SigningStep }
  /** Сертификаты получены: остаётся выбрать один и подтвердить. */
  | {
      readonly phase: "choose";
      readonly certificates: readonly SigningCertificate[];
    }
  | {
      readonly phase: "done";
      readonly signature: DocumentWorkflowSignature;
    }
  | {
      readonly phase: "failed";
      readonly kind: SigningFailureKind;
      /**
       * Подпись создана, но сервер её не принял из-за недоступной проверки.
       * Её можно отправить ещё раз, пока не истёк запрос.
       */
      readonly prepared?: PreparedSignature;
    };

/**
 * Сценарий подписи одной версии.
 *
 * Порядок: плагин → сертификаты → выбор → запрос подписи → содержимое →
 * подпись в плагине → отправка. Сам сценарий живёт в `entities`; здесь только
 * состояние для интерфейса и защита от гонок: результат сброшенного или
 * размонтированного сценария в состояние не попадает.
 */
export function useVersionSigning({
  services,
  version,
  profile,
  capabilities,
  onSigned,
}: {
  services: ShellServices;
  version: Pick<DocumentWorkflowFileVersion, "id" | "sha256">;
  /** Профиль, выбранный по возможностям сервера и браузера. */
  profile: CadesProfile;
  capabilities: Pick<
    DocumentWorkflowSignatureCapabilities,
    "request_ttl_seconds"
  >;
  onSigned: (signature: DocumentWorkflowSignature) => void;
}) {
  const [state, setState] = useState<SigningState>({ phase: "idle" });
  const run = useRef(0);
  const pluginVersion = useRef<string | null>(null);
  const busy = useRef(false);
  const onSignedRef = useRef(onSigned);
  onSignedRef.current = onSigned;

  useEffect(
    () => () => {
      // Размонтирование гасит незавершённый сценарий.
      run.current += 1;
    },
    [],
  );

  const cryptoPro = services.cryptoPro ?? createUnavailableCryptoProAdapter();

  const reset = useCallback(() => {
    run.current += 1;
    busy.current = false;
    setState({ phase: "idle" });
  }, []);

  const start = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const id = ++run.current;
    const live = () => run.current === id;
    try {
      setState({ phase: "working", step: "plugin" });
      const plugin = await cryptoPro.ensureAvailable();
      if (!live()) return;
      pluginVersion.current = plugin.version;

      setState({ phase: "working", step: "certificates" });
      const certificates = await cryptoPro.listCertificates();
      if (!live()) return;

      setState(
        certificates.length === 0
          ? { phase: "failed", kind: "no_certificates" }
          : { phase: "choose", certificates },
      );
    } catch (error) {
      if (live())
        setState({ phase: "failed", kind: classifySigningFailure(error) });
    } finally {
      if (live()) busy.current = false;
    }
  }, [cryptoPro]);

  const send = useCallback(
    async (prepared: PreparedSignature, id: number) => {
      const live = () => run.current === id;
      try {
        setState({ phase: "working", step: "send" });
        const signature = await submitSignature(
          { api: services.documentWorkflow },
          prepared,
        );
        if (!live()) return;
        setState({ phase: "done", signature });
        onSignedRef.current(signature);
      } catch (error) {
        if (!live()) return;
        const kind = classifySigningFailure(error);
        // Nonce тратится только успешной отправкой: при недоступной проверке
        // сервер его не гасит, и подпись можно отправить повторно — но только
        // пока запрос не устарел. Всё остальное требует нового запроса.
        setState(
          kind === "verifier_unavailable" && !isSignatureExpired(prepared)
            ? { phase: "failed", kind, prepared }
            : { phase: "failed", kind },
        );
      } finally {
        if (live()) busy.current = false;
      }
    },
    [services.documentWorkflow],
  );

  const sign = useCallback(
    async (certificateId: string) => {
      if (busy.current) return;
      busy.current = true;
      const id = ++run.current;
      const live = () => run.current === id;
      let prepared: PreparedSignature;
      try {
        setState({ phase: "working", step: "request" });
        prepared = await prepareSignature(
          {
            api: services.documentWorkflow,
            cryptoPro,
          },
          {
            version,
            certificateId,
            profile,
            requestTtlSeconds: capabilities.request_ttl_seconds,
            clientVersion:
              pluginVersion.current === null
                ? "cadesplugin"
                : `cadesplugin/${pluginVersion.current}`,
            onStep: (step) => {
              if (live()) setState({ phase: "working", step });
            },
          },
        );
      } catch (error) {
        if (live()) {
          setState({ phase: "failed", kind: classifySigningFailure(error) });
          busy.current = false;
        }
        return;
      }
      if (!live()) return;
      await send(prepared, id);
    },
    [
      capabilities.request_ttl_seconds,
      cryptoPro,
      profile,
      send,
      services.documentWorkflow,
      version,
    ],
  );

  /** Повторная отправка уже созданной подписи, если запрос ещё действует. */
  const resend = useCallback(async () => {
    if (busy.current || state.phase !== "failed" || !state.prepared) return;
    const { prepared } = state;
    if (isSignatureExpired(prepared)) {
      setState({ phase: "failed", kind: "request_expired" });
      return;
    }
    busy.current = true;
    await send(prepared, ++run.current);
  }, [send, state]);

  return { state, start, sign, resend, reset };
}
